// NYX.Baby Website Context for Chat Assistant
// This file contains internal context information for the chat system
// and is bundled with the application (not publicly accessible via URL)
// 
// Last updated: 2025-09-01 11:48:07

export const NYX_CONTEXT = `# NYX.Baby Website Context Document

## Site Overview
nyx.baby is the public-facing website for NYX NoCode, an AI-powered no-code platform designed for educational hackathons called Hacktopia. The site targets middle and high school educators and students.

## Core Product & Mission
**NYX NoCode** is an AI-powered no-code platform that transforms natural language prompts and doodles into working web applications. Named after Nyx, the Greek Goddess of the Night, it embodies creativity, magic, and empowerment. Students can create without writing code—ideas become prototypes in seconds.

**Hacktopia** is the educational program where NYX NoCode is exclusively available. It brings hackathon energy into classrooms through structured 1-day or 21-day experiences.

## Key Pages (from sitemap.xml priority order)

### 1. Homepage (/) - Priority 1.0
**Purpose**: Main landing and value proposition
**Key Messages**:
- "Turning Students into Future-Ready Creators"
- Students imagine, prompt, and build real apps while teachers guide
- Features prompt-based interface, doodle-to-prompt creativity, visual design, prompt library, reverse engineering mode
- Teachers as co-learners model - educators don't need to be tech experts
- AI collaboration, not replacement - students learn to work WITH AI

**Core Tools Highlighted**:
- Launch Pad: Interactive help files + AI chat advisor
- Doodle Pad: Sketch to text prompt transformation
- Prompt Pad: Creative hub for generating apps/video/experiences
- 3D Pad: Build 3D shapes with images/videos
- Speech Pad: 41 AI voices for narration
- Video Pad: Combine multiple videos and audio
- Gallery: Auto-save all creations

### 2. Hacktopia (/hacktopia) - Priority 0.9
**Purpose**: Core program explanation
**Key Details**:
- NoCode hackathons for classrooms
- Virtual team provides platform, teachers lead hands-on
- Co-hosted by "Justin Mascot" - AI persona adopting school's mascot identity
- Pricing: Demo Day ($500 for up to 200 students), Full Hacktopia ($5,000, 21-day experience)
- Focus: leveling playing field, removing coding barriers

### 3. NYX Catalog (/nyx-catalog) - Priority 0.8
**Purpose**: Gallery of student-generated examples
- Showcases what students can create with NYX NoCode
- External links to blob storage for examples

### 4. Get Started (/get-started) - Priority 0.9
**Purpose**: Lead capture and program initiation
- Main conversion page for educators interested in bringing Hacktopia to their schools

### 5. Demos (/demos) - Priority 0.8
**Purpose**: Interactive demonstrations and examples
- Live demos of NYX NoCode capabilities
- Video content and interactive previews

### 6. How It Works (/how-it-works) - Priority 0.8
**Purpose**: Technical explanation and methodology
- Details on the NYX NoCode platform functionality
- Educational approach and pedagogy

### 7. Press (/press) - Priority 0.7
**Purpose**: Media resources and company information
- Press releases, company background
- Information about founder Karen Kilroy (technologist, educator, author)

## Target Audience
**Primary**: Middle and high school educators
**Secondary**: School administrators, students
**Geographic**: Initially US-focused

## Key Value Propositions
1. **Accessibility**: No coding knowledge required - natural language interface
2. **Inclusivity**: Multi-language support, breaks down barriers
3. **Teacher-Centric**: Educators as co-learners, not tech experts
4. **Real Results**: Professional-looking output that students can be proud of
5. **AI Partnership**: Teaching collaboration with AI, not replacement by AI

## Technical Architecture
- React SPA with React Router DOM
- Azure Static Web Apps deployment
- Integration with Azure services (Cosmos DB, MSAL authentication, Azure Search)
- Microsoft Cognitive Services for speech functionality
- Responsive design with mobile-first approach

## Brand Personality
- Empowering and magical (Nyx goddess theme)
- Educational and approachable
- Innovation-focused but practical
- Supportive of both teachers and students
- Professional yet creative

## Content Themes
- No-code/low-code education
- AI literacy and collaboration
- Project-based learning
- Digital creativity and storytelling
- Teacher empowerment and professional development
- Student agency and creative expression

## Competitive Differentiators
1. Teacher-centric model (vs. student-direct platforms)
2. Classroom hackathon format (vs. individual learning)
3. Custom AI mascot per school for engagement
4. Focus on middle/high school (vs. adult/professional)
5. Prompt-to-prototype speed and simplicity
6. Integration of doodle-to-code functionality

## Call-to-Actions
- Primary: "Get Started with NYX NoCode/Hacktopia"
- Secondary: Exploring demos, viewing student examples
- Contact/scheduling for program implementation

## Response Guidelines for Chat Assistant
When answering questions, provide helpful links to relevant pages using RELATIVE URLs:
- For pricing questions → [Hacktopia pricing](/hacktopia)
- For getting started → [Get Started](/get-started)
- For seeing examples → [NYX Catalog](/nyx-catalog) or [Live Demos](/demos)
- For technical details → [How It Works](/how-it-works)
- For general information → [Homepage](/)
- For media/company info → [Press](/press)

IMPORTANT: When users ask for a link or say "yes" to wanting a link, immediately provide the actual clickable link in markdown format [text](/path). Don't be vague or ask follow-up questions - give them the link they requested.

Always include relevant links when they add value to your response. Format links naturally in your answers using relative paths (starting with /) so they work on any host environment.

This context should help the chat assistant understand the site's purpose, audience, key messages, and how to assist users interested in NYX NoCode and Hacktopia programs.`;
